import React, { useEffect, useMemo, useRef, useState } from "react";

import { Selection, SelectionMode } from "@fluentui/react/lib/Selection";
import { useDispatch } from "react-redux";
import { selectShownClients } from "../../store/clients-page/clientsPage";
import { loadClients } from "../../store/clients/clients";
import { AppDispatch } from "../../store/store";

import { useBoolean, useConst } from "@fluentui/react-hooks";
import { useSelector } from "../../store/hooks";
import { Navigation } from "./Navigation";

import { ContextButtonForTree } from "./ContextButtonForTree";

import { useTranslation } from "react-i18next";
import { ReactComponent as CompanyIcon } from "../../assets/icons/company.svg";
import { useWindowSize } from "../../utils/useWindowSize";
import { SetRatingLimitsDialog } from "./SetRatingLimitsDialog/SetRatingLimitsDialog";
import { SetCostEstimateDialog } from "./SetCostEstimateDialog/SetCostEstimateDialog";

import "./ClientTree.scss";

import {
  ConstrainMode,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  IDetailsHeaderProps,
  IGroupHeaderProps,
  IRenderFunction,
  IconButton,
  ScrollablePane,
  TooltipHost,
} from "@fluentui/react";

import { TableSpinner } from "../../components/TableSpinner";

import { Sticky, StickyPositionType } from "@fluentui/react/lib/Sticky";
import { ReportDocumentDialog } from "./ReportDocumentDialog";
import { UsersReportDocumentDialog } from "./UsersReportDocumentDialog";
import {
  useColumnWidths,
  getDefaultWidthsFromColumns,
} from "../../hooks/useColumnWidths";
import {
  ColumnOptions,
  useColumnOptionsState,
  getActiveFiltersFromColumns,
  hasActiveColumnFilters,
  applyFilterToValue,
} from "../../components/column-options";
import type { Column as ColumnOptionsColumn, Filter } from "../../components/column-options";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { ExportExcelGeneric } from "../../components/ExportExcelGeneric";
import moment from "moment";

export interface IItem {
  key: number;
  name: string;
  companyid: string;
}

export interface IDetailsListDocumentsExampleState {
  columns: IColumn[];
  items: ITree[];
  selectionDetails: string;
  isModalSelection: boolean;
  isCompactMode: boolean;
  announcedMessage?: string;
}

export interface ITree {
  id: number;
  name: string;
  parentid: number;
  startIndex: number;
  count: number;
  level: number;

  children: any[];
  companyid: number;
  countryCodeID: number;
  costCenterCodeID: number;
  goodRangeMin: number;
  moderateRangeMin: number;
  poorRangeMin: number;
  number: number;
  data?: any[];
}

export interface IButtonExampleProps {
  disabled?: boolean;
  checked?: boolean;
}

export interface IWidths {
  col: {
    key: string;
    value: number;
  }[];
}

/**
 * Generate dummy tree data for testing
 */
const generateDummyTree = (
  rootCount: number,
  minChildren: number,
  maxChildren: number,
  maxDepth: number,
): ITree[] => {
  const items: ITree[] = [];
  let currentId = 1;

  const randomInt = (min: number, max: number): number => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  const createNode = (parentId: number | null, level: number): ITree => {
    const nodeId = currentId++;
    const childCount =
      level < maxDepth ? randomInt(minChildren, maxChildren) : 0;
    const countryId = randomInt(1, 10);
    const costCenterId = randomInt(1, 5);
    const goodRange = randomInt(80, 100);
    const moderateRange = randomInt(50, 79);
    const poorRange = randomInt(0, 49);

    const node: ITree = {
      id: nodeId,
      name: `Client ${nodeId}`,
      parentid: parentId || 0,
      startIndex: 0,
      count: 0,
      level: level,
      children: [],
      companyid: nodeId,
      countryCodeID: countryId,
      costCenterCodeID: costCenterId,
      goodRangeMin: goodRange,
      moderateRangeMin: moderateRange,
      poorRangeMin: poorRange,
      number: nodeId,
      data: [
        { number: 0 }, //0
        { level: level }, //1
        { companyName: `Client ${nodeId}` }, //2
        { companyId: nodeId }, //3
        { parentCompany: parentId || 0 }, //4
        { country: countryId }, //5
        { costCenter: costCenterId }, //6
        { goodRate: goodRange }, //7
        { moderateRate: moderateRange }, //8
        { poorRate: poorRange }, //9
      ],
    };

    items.push(node);

    for (let i = 0; i < childCount; i++) {
      const childNode = createNode(nodeId, level + 1);
      node.children.push(childNode);
    }

    return node;
  };

  for (let i = 0; i < rootCount; i++) {
    createNode(null, 0);
  }

  return items;
};

const allExcelColumns = [
  { key: "clientName", labelKey: "ram.clientsList.clientName", width: 250 },
  { key: "clientId", labelKey: "ram.clientsList.clientId", width: 150 },
  { key: "countryCodeID", labelKey: "ram.clientList.countryCode", width: 100 },
  {
    key: "costCenterCodeID",
    labelKey: "ram.clientsList.costCenterCode",
    width: 150,
  },
  { key: "goodRangeMin", labelKey: "ram.clientsList.goodRangeMin", width: 70 },
  {
    key: "moderateRangeMin",
    labelKey: "ram.clientsList.moderateRangeMin",
    width: 70,
  },
  { key: "poorRangeMin", labelKey: "ram.clientsList.poorRangeMin", width: 70 },
  {
    key: "mediumCostEstimateMin",
    labelKey: "ram.clientsList.mediumLowThreshold",
    width: 70,
  },
  {
    key: "highCostEstimateMin",
    labelKey: "ram.clientsList.highMediumThreshold",
    width: 70,
  },
];

// Column-to-node-property mapping for tree-aware filtering
const getNodeValue = (node: any, columnKey: string): any => {
  switch (columnKey) {
    case "name":
      return node.name || node.data?.[2]?.companyName;
    case "companyId":
      return node.companyid || node.data?.[3]?.companyId;
    case "country":
      return node.countryCodeID || node.data?.[5]?.country;
    case "costCenter":
      return node.costCenterCodeID || node.data?.[6]?.costCenter;
    case "goodRateMin":
      return node.goodRangeMin || node.data?.[7]?.goodRate;
    case "moderateRateMin":
      return node.moderateRangeMin || node.data?.[8]?.moderateRate;
    case "poorRateMin":
      return node.poorRangeMin || node.data?.[9]?.poorRate;
    default:
      return node[columnKey];
  }
};

/** Column definitions for the ColumnOptions panel */
const getColumnOptionsColumns = (t: (key: string) => string): ColumnOptionsColumn[] => [
  // name (company name) and column2 (kebab) are not in column options
  { key: "companyId", label: t("ram.clientsList.clientId"), isVisible: true, filter: { type: "text", value: "" } },
  { key: "country", label: t("ram.clientList.countryCode"), isVisible: true, filter: { type: "taxonomy", taxonomyKey: "Country", value: [] } },
  { key: "costCenter", label: t("ram.clientsList.costCenterCode"), isVisible: true, filter: { type: "taxonomy", taxonomyKey: "CostCenter", value: [] } },
  { key: "goodRateMin", label: t("ram.clientsList.goodRangeMin"), isVisible: true, filter: { type: "number", operator: "gt", number1: null, number2: null } },
  { key: "moderateRateMin", label: t("ram.clientsList.moderateRangeMin"), isVisible: true, filter: { type: "number", operator: "gt", number1: null, number2: null } },
  { key: "poorRateMin", label: t("ram.clientsList.poorRangeMin"), isVisible: true, filter: { type: "number", operator: "gt", number1: null, number2: null } },
  { key: "mediumCostEstimateMin", label: t("ram.clientsList.mediumLowThreshold"), isVisible: true, filter: { type: "number", operator: "gt", number1: null, number2: null } },
  { key: "highCostEstimateMin", label: t("ram.clientsList.highMediumThreshold"), isVisible: true, filter: { type: "number", operator: "gt", number1: null, number2: null } },
];

export let ClientsTree = () => {
  const clients = useSelector(selectShownClients as any) as any[];
  const { t } = useTranslation();
  const taxonomy = useSelector((s) => s.taxonomy);
  const company = useSelector((s) => s.clients);
  const [dark, setDark] = useState(true);
  const refExcelExport = useRef<ExcelExport>();
  const [items, setItems] = useState([]);

  // Column options (replaces old column visibility, filtering, and reordering)
  // Only include taxonomy values that actually appear in the current dataset
  const columnOptionsTaxonomy = useMemo(() => {
    const result: { [key: string]: { code: string; id: number }[] } = {};
    for (const key of Object.keys(taxonomy)) {
      if (key === "response" || key === "isLoading" || key === "isLoadingUAD") continue;
      result[key] = (taxonomy as any)[key]?.items || [];
    }

    // Collect IDs present in the data
    const usedCountryIds = new Set<number>();
    const usedCostCenterIds = new Set<number>();
    for (const item of items) {
      if (item.countryCodeID != null) usedCountryIds.add(item.countryCodeID);
      if (item.costCenterCodeID != null) usedCostCenterIds.add(item.costCenterCodeID);
    }

    // Filter taxonomy lists to only values present in data
    if (result.Country) {
      result.Country = result.Country.filter((t) => usedCountryIds.has(t.id));
    }
    if (result.CostCenter) {
      result.CostCenter = result.CostCenter.filter((t) => usedCostCenterIds.has(t.id));
    }

    return result;
  }, [taxonomy, items]);

  const columnOptionsDefaultColumns = useMemo(
    () => getColumnOptionsColumns(t),
    [t],
  );

  const {
    isPanelOpen,
    openPanel,
    closePanel: closeColumnOptions,
    columns: draftColumns,
    views,
    selectedView,
    isReady,
    isViewLoading,
    onClickApply,
    onChangeView,
    setFilter: setColumnOptionsFilter,
    onSelectAll,
    onDeselectAll,
    onReorderColumn,
    onToggleColumnVisibility,
    onClickDeleteView,
    onClickClearAllFilters,
    onClickReset,
    onClickSaveView,
    onCreateView,
    onUpdateView,
    onResetAllStorage,
    activeColumns,
    applyFiltersToData,
    activeFilterCount,
  } = useColumnOptionsState({
    tableId: "clients-tree",
    defaultColumns: columnOptionsDefaultColumns,
    taxonomy: columnOptionsTaxonomy,
  });

  // Get visible ordered column keys (for rendering toggleable columns)
  const visibleOrderedKeys = useMemo(
    () => activeColumns.filter((c) => c.isVisible).map((c) => c.key),
    [activeColumns],
  );

  // Compute visible label keys in column order for export
  const visibleLabelKeys = useMemo(() => {
    const keyToLabel: Record<string, string> = {
      companyId: "ram.clientsList.clientId",
      country: "ram.clientList.countryCode",
      costCenter: "ram.clientsList.costCenterCode",
      goodRateMin: "ram.clientsList.goodRangeMin",
      moderateRateMin: "ram.clientsList.moderateRangeMin",
      poorRateMin: "ram.clientsList.poorRangeMin",
      mediumCostEstimateMin: "ram.clientsList.mediumLowThreshold",
      highCostEstimateMin: "ram.clientsList.highMediumThreshold",
    };
    return visibleOrderedKeys.map((k) => keyToLabel[k]).filter(Boolean);
  }, [visibleOrderedKeys]);

  // clientName is always visible (non-toggleable in table but included in export)
  const excelColumns = useMemo(() => {
    const visible = allExcelColumns.filter(
      (c) =>
        c.labelKey === "ram.clientsList.clientName" ||
        visibleLabelKeys.includes(c.labelKey),
    );
    return visible.sort((a, b) => {
      if (a.labelKey === "ram.clientsList.clientName") return -1;
      if (b.labelKey === "ram.clientsList.clientName") return 1;
      return (
        visibleLabelKeys.indexOf(a.labelKey) -
        visibleLabelKeys.indexOf(b.labelKey)
      );
    });
  }, [visibleLabelKeys]);

  const fileName = useMemo(() => {
    return "Clients" + moment(new Date()).format("_YYYY_MM_DD_HH_mm_ss");
  }, []);

  // Default column definitions (static, used for width extraction)
  const defaultColumnDefs = useMemo(
    () => [
      { key: "name", width: 303 },
      { key: "column2", width: 12 },
      { key: "companyId", width: 233 },
      { key: "country", width: 233 },
      { key: "costCenter", width: 233 },
      { key: "goodRateMin", width: 133 },
      { key: "moderateRateMin", width: 133 },
      { key: "poorRateMin", width: 133 },
      { key: "highCostEstimateMin", width: 133 },
      { key: "mediumCostEstimateMin", width: 133 },
    ],
    [],
  );

  // Initialize column widths with localStorage persistence
  const defaultWidths = useMemo(
    () => getDefaultWidthsFromColumns(defaultColumnDefs),
    [defaultColumnDefs],
  );
  const { columnWidths, updateColumnWidth } = useColumnWidths({
    storageKey: "clients-tree",
    defaultWidths,
  });

  // Extract active filters from column options for tree-aware filtering
  const activeFilters = useMemo(
    () => getActiveFiltersFromColumns(activeColumns),
    [activeColumns],
  );
  const hasFilters = useMemo(
    () => hasActiveColumnFilters(activeColumns),
    [activeColumns],
  );

  const [groups, setGroups] = useState(null);
  const [firstGroup, setFirstGroup] = useState(null);
  const [collapsedIds, setCollapsedIds] = useState<Set<number>>(new Set());
  const dispatch: AppDispatch = useDispatch();
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const reportDocumentDialog = useSelector(
    (s) => s.clientsPage.reportDocumentDialog,
  );
  const filteredClients = Object.values(useSelector((s) => s.clients.entities));

  const usersReportDocumentDialog = useSelector(
    (s) => s.clientsPage.usersReportDocumentDialog,
  );

  const populateDummyTree = (
    rootCount: number = 10,
    minChildren: number = 0,
    maxChildren: number = 5,
    maxDepth: number = 4,
  ): void => {
    const dummyItems = generateDummyTree(
      rootCount,
      minChildren,
      maxChildren,
      maxDepth,
    );
    setItems(dummyItems);
    console.log(
      `Generated ${dummyItems.length} dummy tree items (${rootCount} root items) with max depth ${maxDepth}`,
    );
  };

  useEffect(() => {
    (window as any).populateDummyTree = populateDummyTree;
    return () => {
      delete (window as any).populateDummyTree;
    };
  }, []);

  useEffect(() => {
    dispatch(loadClients.trigger());
  }, []);

  // Build DetailsList column definitions (name + column2 always first, then visible ordered)
  const allColumns = useMemo(
    () => [
      {
        key: "name",
        name: t("ram.clientsList.clientName"),
        fieldName: "name",
        minWidth: Math.min(columnWidths["name"] || 303, 50),
        maxWidth: 1000,
        isResizable: true,
        calculatedWidth: columnWidths["name"] || 303,
        styles: { cellTitle: { justifyContent: "left" } },
      },
      {
        key: "column2",
        name: "",
        fieldName: "companyName",
        minWidth: 12,
        maxWidth: 12,
        isResizable: false,
        isSorted: false,
        styles: { cellTitle: { justifyContent: "left" } },
      },
      {
        key: "companyId",
        name: t("ram.clientsList.clientId"),
        fieldName: "companyId",
        minWidth: Math.min(columnWidths["companyId"] || 233, 50),
        maxWidth: 1000,
        isResizable: true,
        calculatedWidth: columnWidths["companyId"] || 233,
        isSorted: false,
        styles: { cellTitle: { justifyContent: "center" } },
      },
      {
        key: "country",
        name: t("ram.clientList.countryCode"),
        fieldName: "country",
        minWidth: Math.min(columnWidths["country"] || 233, 50),
        maxWidth: 1000,
        isResizable: true,
        calculatedWidth: columnWidths["country"] || 233,
        isSorted: false,
        styles: { cellTitle: { justifyContent: "center" } },
      },
      {
        key: "costCenter",
        name: t("ram.clientsList.costCenterCode"),
        fieldName: "costCenter",
        minWidth: Math.min(columnWidths["costCenter"] || 233, 50),
        maxWidth: 1000,
        isResizable: true,
        calculatedWidth: columnWidths["costCenter"] || 233,
        isSorted: false,
        styles: { cellTitle: { justifyContent: "center" } },
      },
      {
        key: "goodRateMin",
        name: t("ram.clientsList.goodRangeMin"),
        fieldName: "goodRateMin",
        minWidth: Math.min(columnWidths["goodRateMin"] || 233, 50),
        maxWidth: 1000,
        isResizable: true,
        calculatedWidth: columnWidths["goodRateMin"] || 233,
        isSorted: false,
        styles: { cellTitle: { justifyContent: "center" } },
      },
      {
        key: "moderateRateMin",
        name: t("ram.clientsList.moderateRangeMin"),
        fieldName: "moderateRateMin",
        minWidth: Math.min(columnWidths["moderateRateMin"] || 233, 50),
        maxWidth: 1000,
        isResizable: true,
        calculatedWidth: columnWidths["moderateRateMin"] || 233,
        isSorted: false,
        styles: { cellTitle: { justifyContent: "center" } },
      },
      {
        key: "poorRateMin",
        name: t("ram.clientsList.poorRangeMin"),
        fieldName: "poorRateMin",
        minWidth: Math.min(columnWidths["poorRateMin"] || 233, 50),
        maxWidth: 1000,
        isResizable: true,
        calculatedWidth: columnWidths["poorRateMin"] || 233,
        isSorted: false,
        styles: { cellTitle: { justifyContent: "center" } },
      },
      {
        key: "mediumCostEstimateMin",
        name: t("ram.clientsList.mediumLowThreshold"),
        fieldName: "mediumCostEstimateMin",
        minWidth: Math.min(columnWidths["mediumCostEstimateMin"] || 233, 50),
        maxWidth: 1000,
        isResizable: true,
        calculatedWidth: columnWidths["mediumCostEstimateMin"] || 233,
        isSorted: false,
        styles: { cellTitle: { justifyContent: "center" } },
      },
      {
        key: "highCostEstimateMin",
        name: t("ram.clientsList.highMediumThreshold"),
        fieldName: "highCostEstimateMin",
        minWidth: Math.min(columnWidths["highCostEstimateMin"] || 233, 50),
        maxWidth: 1000,
        isResizable: true,
        calculatedWidth: columnWidths["highCostEstimateMin"] || 233,
        isSorted: false,
        styles: { cellTitle: { justifyContent: "center" } },
      },
    ],
    [t, columnWidths],
  );

  // Filter and reorder columns based on activeColumns; name and column2 always first
  const columns = useMemo(() => {
    const allColumnsMap = new Map(allColumns.map((c) => [c.key, c]));
    const fixed = ["name", "column2"]
      .map((k) => allColumnsMap.get(k))
      .filter(Boolean) as IColumn[];
    const toggleable = visibleOrderedKeys
      .map((k) => allColumnsMap.get(k))
      .filter(Boolean) as IColumn[];
    return [...fixed, ...toggleable];
  }, [allColumns, visibleOrderedKeys]);

  const companyNameRef = useRef(null);

  useEffect(() => {
    if (localStorage.getItem("darkMode") === "true") {
      setDark(true);
    } else {
      setDark(false);
    }
  });

  const setRatingLimitsDialog = useSelector(
    (s) => s.clientsPage.setRatingLimitsDialog,
  );

  const setCostEstimateDialog = useSelector(
    (s) => s.clientsPage.setCostEstimateDialog,
  );

  useEffect(() => {
    let newArr = [...searchParentNodes(), ...searchChildrenNodes()];
    newArr = uniqueTreeElements(newArr, (it) => it.clientId);

    setItems(
      newArr.map((c, index) => {
        return {
          id: c.clientId,
          name: c.clientName,
          parentid: c.parentClientId,
          startIndex: 0,
          count: 0,
          level: 0,
          isCollapsed: false,
          children: [],
          companyid: c.clientId,
          countryCodeID: c.countryCodeID,
          costCenterCodeID: c.costCenterCodeID,
          goodRangeMin: c.goodRangeMin,
          moderateRangeMin: c.moderateRangeMin,
          poorRangeMin: c.poorRangeMin,
          highCostEstimateMin: c.highCostEstimateMin,
          mediumCostEstimateMin: c.mediumCostEstimateMin,
          data: [
            { number: 0 }, //0
            { level: 0 }, //1
            { companyName: c.clientName }, //2
            { companyId: c.clientId }, //3
            { parentCompany: c.parentClientId }, //4
            { country: c.countryCodeID }, //5
            { costCenter: c.costCenterCodeID }, //6
            { goodRate: c.goodRangeMin }, //7
            { moderateRate: c.moderateRangeMin }, //8
            { poorRate: c.poorRangeMin }, //9
            { highCostEstimate: c.highCostEstimateMin }, //10
            { mediumCostEstimate: c.mediumCostEstimateMin }, //11
          ],
        };
      }),
    );

    if (firstGroup === null && groups !== null && groups.length !== 0) {
      setFirstGroup(groups);
    }
  }, [clients]);

  useEffect(() => {
    const tree = createTree(items);
    const filteredTree = filterTreeWithParentPreservation(tree);
    setGroups(filteredTree);
  }, [items, activeFilters, collapsedIds]);

  // Count total nodes in the filtered tree for search results display
  const filteredTreeNodeCount = useMemo(() => {
    if (!groups || !hasFilters) return undefined;
    const countNodes = (nodes: any[]): number =>
      nodes.reduce((sum, n) => sum + 1 + countNodes(n.children || []), 0);
    return countNodes(groups);
  }, [groups, hasFilters]);

  //---------------------------SEARCHING AND FILTERING FUNCTIONS----------------------------------------------------

  const searchChildrenNodes = () => {
    let tree = [];
    clients
      ?.filter((item) => item !== undefined)
      .forEach((c) => {
        tree.push(c);
        fillInChildren(c, tree);
      });

    return tree;
  };

  const fillInChildren = (topc, children) => {
    let immediateChildren = [];
    filteredClients.map((f) => {
      if (topc.clientId === f.parentClientId) {
        immediateChildren.push(f);
        if (!children.includes(f)) {
          children.push(f);
        }
      }
    });

    immediateChildren.forEach((ic) => {
      fillInChildren(ic, children);
    });
  };

  function getDepth(array) {
    return (
      1 + Math.max(0, ...array.map(({ children = [] }) => getDepth(children)))
    );
  }

  const searchParentNodes = () => {
    let tree = [];

    clients
      .filter((item) => item !== undefined)
      .map((c) => {
        if (!tree.includes(c)) {
          tree.push(c);
        }
        var node = filteredClients.find((x) => x.clientId === c.parentClientId);
        if (node !== undefined && !tree.includes(node)) {
          tree.push(node);
        }
        if (node !== undefined) {
          while (node.parentClientId !== null) {
            node = filteredClients.find(
              (x) => x.clientId === node.parentClientId,
            );
            if (!tree.includes(node)) {
              tree.push(node);
            }
          }
        } else {
          return null;
        }
      });
    return tree;
  };

  function uniqueTreeElements(data, key) {
    return [...new Map(data.map((x) => [key(x), x])).values()];
  }

  // -------------------------FUNCTIONS FOR DEEP NESTING--------------------------------------------
  function depthDecorator(array, level = 0) {
    return array.map((child) =>
      Object.assign(child, {
        level,
        children: depthDecorator(child.children || [], level + 1),
      }),
    );
  }

  function numberNodes(tree, n = 0) {
    return (function recurse(children) {
      return children.map((node) =>
        Object.assign({}, node, {
          data: [...node.data, { number: n++ }],
          children: recurse(node.children),
        }),
      );
    })(tree);
  }

  const createTree = (arr) => {
    var tree = [],
      mappedArr = {},
      arrElem,
      mappedElem;

    for (var i = 0, len = arr.length; i < len; i++) {
      arrElem = arr[i];
      mappedArr[arrElem.id] = arrElem;
      mappedArr[arrElem.id]["children"] = [];
    }

    for (var id in mappedArr) {
      if (mappedArr.hasOwnProperty(id)) {
        mappedElem = mappedArr[id];

        if (mappedElem.parentid) {
          mappedArr[mappedElem["parentid"]]["children"].push(mappedElem);
        } else {
          tree.push(mappedElem);
        }
      }
    }

    tree.sort(function (x, y) {
      let a = x.name?.toUpperCase(),
        b = y.name?.toUpperCase();
      return a == b ? 0 : a > b ? 1 : -1;
    });

    tree = depthDecorator(tree);
    tree = numberNodes(tree);

    return tree;
  };

  // Tree-aware filter function using ColumnOptions filter state
  const filterTreeWithParentPreservation = (tree: any[]): any[] => {
    const applyCollapseState = (nodes: any[]): any[] => {
      return nodes.map((node) => ({
        ...node,
        isCollapsed: collapsedIds.has(node.id),
        children: node.children ? applyCollapseState(node.children) : [],
      }));
    };

    if (!hasFilters) {
      return applyCollapseState(tree);
    }

    // Check if a single node matches all active filters
    const nodeMatchesFilter = (node: any): boolean => {
      for (const [columnKey, filter] of Object.entries(activeFilters)) {
        const value = getNodeValue(node, columnKey);
        if (!applyFilterToValue(value, filter)) {
          return false;
        }
      }
      return true;
    };

    // Recursively filter tree, preserving parents of matched nodes
    const filterNode = (node: any): any | null => {
      const filteredChildren = (node.children || [])
        .map((child: any) => filterNode(child))
        .filter((child: any) => child !== null);

      const thisNodeMatches = nodeMatchesFilter(node);

      if (thisNodeMatches || filteredChildren.length > 0) {
        return {
          ...node,
          children: filteredChildren,
          isCollapsed: collapsedIds.has(node.id),
        };
      }

      return null;
    };

    return tree
      .map((rootNode) => filterNode(rootNode))
      .filter((node) => node !== null);
  };

  //-----------------------------COLUMNS FOR LIST-----------------------------------

  const [isCompactMode] = useBoolean(false);
  const selection = useConst(() => {
    const s = new Selection();
    s.setItems(items, true);
    return s;
  });

  //-----------------------------ROW RENDERING--------------------------------------------
  const onRenderHeader = (props?: IGroupHeaderProps): JSX.Element | null => {
    if (props) {
      const toggleCollapse = (): void => {
        const group = props.group as any;
        if (group?.id) {
          setCollapsedIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(group.id)) {
              newSet.delete(group.id);
            } else {
              newSet.add(group.id);
            }
            return newSet;
          });
        }
      };

      // Get column widths for row cells - add 20px to match header cell padding
      const groupExpanderWidth = 36;
      const nameWidth = (columnWidths["name"] || 303) + 20;
      const column2Width = 12 + 20;
      const levelPadding = (props.group?.level || 0) * 7;

      // Width map for toggleable columns
      const widthMap: Record<string, number> = {
        companyId: (columnWidths["companyId"] || 233) + 20,
        country: (columnWidths["country"] || 233) + 20,
        costCenter: (columnWidths["costCenter"] || 233) + 20,
        goodRateMin: (columnWidths["goodRateMin"] || 233) + 20,
        moderateRateMin: (columnWidths["moderateRateMin"] || 233) + 20,
        poorRateMin: (columnWidths["poorRateMin"] || 233) + 20,
        highCostEstimateMin: (columnWidths["highCostEstimateMin"] || 233) + 20,
        mediumCostEstimateMin: (columnWidths["mediumCostEstimateMin"] || 233) + 20,
      };

      const cellStyle = (width: number): React.CSSProperties => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: width,
        minWidth: width,
        maxWidth: width,
        flexShrink: 0,
        flexGrow: 0,
        height: 40,
        boxSizing: "border-box",
        overflow: "hidden",
      });

      const renderToggleableCell = (key: string) => {
        const w = widthMap[key];
        if (!w) return null;
        switch (key) {
          case "companyId":
            return (
              <span key={key} style={cellStyle(w)}>
                {props.group?.data[3].companyId}
              </span>
            );
          case "country":
            return (
              <span key={key} style={cellStyle(w)}>
                {t(
                  taxonomy.Country.byId[props.group?.data[5].country]
                    ?.code,
                )}
              </span>
            );
          case "costCenter":
            return (
              <span key={key} style={cellStyle(w)}>
                {props.group?.data[6].costCenter !== null ? (
                  t(
                    taxonomy.CostCenter.byId[
                      props.group?.data[6].costCenter
                    ]?.code,
                  )
                ) : (
                  <span className="item__misingMandate">
                    {t("ram.clientsList.missingMandate")}
                  </span>
                )}
              </span>
            );
          case "goodRateMin":
            return (
              <span key={key} style={cellStyle(w)}>
                {props.group?.data[7].goodRate}
              </span>
            );
          case "moderateRateMin":
            return (
              <span key={key} style={cellStyle(w)}>
                {props.group?.data[8].moderateRate}
              </span>
            );
          case "poorRateMin":
            return (
              <span key={key} style={cellStyle(w)}>
                {props.group?.data[9].poorRate}
              </span>
            );
          case "highCostEstimateMin":
            return (
              <span key={key} style={cellStyle(w)}>
                {props.group?.data[10]?.highCostEstimate}
              </span>
            );
          case "mediumCostEstimateMin":
            return (
              <span key={key} style={cellStyle(w)}>
                {props.group?.data[11]?.mediumCostEstimate}
              </span>
            );
          default:
            return null;
        }
      };

      return (
        <div
          className={dark ? "item_dark" : "item"}
          style={
            dark
              ? {
                  backgroundColor:
                    props.group.data[10].number % 2 ? "#171717" : "#02070a",
                }
              : {
                  backgroundColor:
                    props.group.data[10].number % 2 ? "#FFFFFF" : "#F1F7FB",
                }
          }
        >
          {/* Group expander */}
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: groupExpanderWidth,
              minWidth: groupExpanderWidth,
              flexShrink: 0,
              paddingLeft: levelPadding,
            }}
          >
            {props.group?.isCollapsed ? (
              props.group?.children.length !== 0 ? (
                <IconButton
                  className="item__arrowicon"
                  iconProps={{ iconName: "ChevronRightMed" }}
                  title="Expand"
                  ariaLabel="Expand"
                  onClick={toggleCollapse}
                  styles={{ root: { width: 32, height: 32 } }}
                />
              ) : (
                <CompanyIcon
                  style={{
                    width: 16,
                    height: 16,
                    flexShrink: 0,
                  }}
                />
              )
            ) : props.group?.children.length !== 0 ? (
              <IconButton
                className="item__arrowicon"
                iconProps={{ iconName: "ChevronDownMed" }}
                title="Collapse"
                ariaLabel="Collapse"
                onClick={toggleCollapse}
                styles={{ root: { width: 32, height: 32 } }}
              />
            ) : (
              <CompanyIcon
                style={{
                  width: 16,
                  height: 16,
                  flexShrink: 0,
                }}
              />
            )}
          </span>

          {/* Company name (always first) */}
          <span
            style={{
              ...cellStyle(nameWidth),
              justifyContent: "flex-start",
            }}
          >
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {props.group?.data[2].companyName}
            </span>
          </span>

          {/* Kebab menu (always second) */}
          <span
            style={{
              ...cellStyle(column2Width),
              padding: 0,
            }}
          >
            <ContextButtonForTree
              clientId={props.group?.data[3].companyId}
              poorRangeMin={props.group?.data[9].poorRate}
              moderateRangeMin={props.group?.data[8].moderateRate}
              goodRangeMin={props.group?.data[7].goodRate}
              highCostEstimateMin={props.group?.data[10]?.highCostEstimate}
              mediumCostEstimateMin={props.group?.data[11]?.mediumCostEstimate}
            />
          </span>

          {/* Toggleable columns in user-defined order */}
          {visibleOrderedKeys.map((key) => renderToggleableCell(key))}
        </div>
      );
    }

    return null;
  };

  // Helper function to get all node IDs recursively
  const getAllNodeIds = (nodes: any[]): number[] => {
    let ids: number[] = [];
    for (const node of nodes) {
      ids.push(node.id);
      if (node.children && node.children.length > 0) {
        ids = ids.concat(getAllNodeIds(node.children));
      }
    }
    return ids;
  };

  const groupedListProps = {
    onRenderHeader,
    showEmptyGroups: true,
    onToggleCollapseAll: (isAllCollapsed: boolean) => {
      if (isAllCollapsed) {
        const allIds = getAllNodeIds(groups || []);
        setCollapsedIds(new Set(allIds));
      } else {
        setCollapsedIds(new Set());
      }
    },
  };

  const onRenderDetailsHeader: IRenderFunction<IDetailsHeaderProps> = (
    props,
    defaultRender,
  ) => {
    if (!props) {
      return null;
    }

    return (
      <Sticky
        stickyClassName="sticky"
        stickyPosition={StickyPositionType.Header}
        isScrollSynced
      >
        {defaultRender!({
          styles: { root: { paddingTop: 0 } },
          ...props,
        })}
      </Sticky>
    );
  };

  const areClientsLoading = useSelector((s) => s.clients.isLoading);

  return (
    <div className="clients_page">
      <Navigation
        refExcelExport={refExcelExport?.current}
        filteredClients={clients}
        visibleLabelKeys={visibleLabelKeys}
        onOpenColumnOptions={openPanel}
        activeFilterCount={activeFilterCount}
        filteredCount={filteredTreeNodeCount}
      />
      {isPanelOpen && (
        <ColumnOptions
          closePanel={closeColumnOptions}
          columns={draftColumns}
          onClickApply={onClickApply}
          views={views}
          selectedView={selectedView}
          onChangeView={onChangeView}
          isReady={isReady}
          isViewLoading={isViewLoading}
          setFilter={setColumnOptionsFilter}
          onSelectAll={onSelectAll}
          onDeselectAll={onDeselectAll}
          taxonomy={columnOptionsTaxonomy}
          onClickDeleteView={onClickDeleteView}
          onClickClearAllFilters={onClickClearAllFilters}
          onClickReset={onClickReset}
          onClickSaveView={onClickSaveView}
          onCreateView={onCreateView}
          onUpdateView={onUpdateView}
          onReorderColumn={onReorderColumn}
          onToggleColumnVisibility={onToggleColumnVisibility}
          onResetAllStorage={onResetAllStorage}
          t={t}
        />
      )}
      {
        clients && clients?.length > 0 ? (
          <div
            style={{
              position: "relative",
              height: windowHeight - 97,
            }}
          >
            <ScrollablePane
              styles={{
                stickyAbove: { zIndex: 3 },
              }}
            >
            <div className="item_list" ref={companyNameRef}>
              <DetailsList
                items={items}
                columns={columns}
                isHeaderVisible={true}
                selection={selection}
                selectionMode={SelectionMode.none}
                groups={groups}
                onRenderDetailsHeader={onRenderDetailsHeader}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                constrainMode={ConstrainMode.horizontalConstrained}
                compact={true}
                onShouldVirtualize={() => false}
                selectionPreservedOnEmptyClick
                ariaLabelForSelectionColumn="Toggle selection"
                ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                onColumnResize={(column, newWidth) => {
                  if (column?.key && column.key !== "column2") {
                    updateColumnWidth(column.key, newWidth ?? 100);
                  }
                }}
                groupProps={groupedListProps}
                styles={{
                  headerWrapper: {
                    width: "100%",
                    paddingTop: 0,
                    zIndex: 1,
                    selectors: {
                      div: { position: "relative", paddingTop: 0 },
                    },
                  },
                  focusZone: {},
                  contentWrapper: { paddingBottom: 5 },
                  root: { flexShrink: 0, marginBottom: 0, overflow: "visible" },
                }}
              />
            </div>
            </ScrollablePane>
            {areClientsLoading && <TableSpinner />}
            {setRatingLimitsDialog.isOpen && <SetRatingLimitsDialog />}
            {setCostEstimateDialog.isOpen && <SetCostEstimateDialog />}
            {reportDocumentDialog.isOpen && <ReportDocumentDialog />}
            {usersReportDocumentDialog.isOpen && <UsersReportDocumentDialog />}
          </div>
        ) : null
      }
      <ExportExcelGeneric
        ref={refExcelExport}
        columns={excelColumns}
        fileName={fileName}
      />
    </div>
  );
};
